import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useCreateDataAgentExampleQueryMutation,
  useUpdateDataAgentExampleQueryMutation,
} from '../../../../api/dataAgentApiSlice';
import { showToaster } from '../../../../utils/globalImports';

interface DataAgentExampleQueryValues {
  dataAgentId: string;
  customerId: string;
  clientId: string;
  question: string;
  query: string;
  queryLanguage: string;
  description?: string;
  expectedResult?: string;
  isActive: boolean;
}

export const useDataAgentExampleQueryManagement = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [createDataAgentExampleQuery] = useCreateDataAgentExampleQueryMutation();
  const [updateDataAgentExampleQuery] = useUpdateDataAgentExampleQueryMutation();

  const validationSchema = Yup.object().shape({
    question: Yup.string().required(t('dataAgents.exampleQueries.validation.questionRequired')),
    query: Yup.string().required(t('dataAgents.exampleQueries.validation.queryRequired')),
    queryLanguage: Yup.string().required(t('dataAgents.exampleQueries.validation.queryLanguageRequired')),
    description: Yup.string(),
    expectedResult: Yup.string(),
  });

  const handleSubmit = async (values: DataAgentExampleQueryValues) => {
    const payload = {
      data_agent_id: values.dataAgentId,
      customer_id: values.customerId,
      client_id: values.clientId,
      question: values.question,
      query: values.query,
      query_language: values.queryLanguage,
      description: values.description || '',
      expected_result: values.expectedResult || '',
      is_active: values.isActive,
    };

    console.log('Create Example Query Payload:', payload);

    try {
      const response = await createDataAgentExampleQuery(payload).unwrap();

      dispatch(
        showToaster({
          message: t('dataAgents.exampleQueries.apiMessages.exampleQueryCreatedSuccess'),
          type: 'success',
        }),
      );

      return { success: true, data: response };
    } catch (error: any) {
      console.error('Failed to create example query:', error);

      dispatch(
        showToaster({
          message: error?.data?.message || t('dataAgents.exampleQueries.apiMessages.exampleQueryCreatedError'),
          type: 'error',
        }),
      );

      return { success: false, error };
    }
  };

  const handleUpdate = async (dataAgentExampleQueryId: string, values: DataAgentExampleQueryValues) => {
    const payload = {
      data_agent_example_query_id: dataAgentExampleQueryId,
      data_agent_id: values.dataAgentId,
      customer_id: values.customerId,
      client_id: values.clientId,
      question: values.question,
      query: values.query,
      query_language: values.queryLanguage,
      description: values.description || '',
      expected_result: values.expectedResult || '',
      is_active: values.isActive,
    };

    console.log('Update Example Query Payload:', payload);

    try {
      const response = await updateDataAgentExampleQuery(payload).unwrap();

      dispatch(
        showToaster({
          message: t('dataAgents.exampleQueries.apiMessages.exampleQueryUpdatedSuccess'),
          type: 'success',
        }),
      );

      return { success: true, data: response };
    } catch (error: any) {
      console.error('Failed to update example query:', error);

      dispatch(
        showToaster({
          message: error?.data?.message || t('dataAgents.exampleQueries.apiMessages.exampleQueryUpdatedError'),
          type: 'error',
        }),
      );

      return { success: false, error };
    }
  };

  return { validationSchema, handleSubmit, handleUpdate };
};
